/** Géométrie de circuit. Pure (zéro Phaser/DOM), donc testable en Node et déterministe. */

export interface Vec2 {
  x: number;
  y: number;
}

/** Définition d'un circuit : une centerline fermée + une demi-largeur. */
export interface TrackDef {
  name: string;
  /** Points de la centerline, boucle fermée (le dernier rejoint le premier). */
  centerline: Vec2[];
  /** Demi-largeur de la piste (px). */
  halfWidth: number;
  /** Nombre de tours pour gagner. */
  laps: number;
  /** Nombre de checkpoints (portes) répartis le long du tour. */
  checkpointCount: number;
}

interface Sample {
  x: number;
  y: number;
  /** Tangente unitaire (sens de la course). */
  tx: number;
  ty: number;
  /** Normale unitaire (gauche de la tangente). */
  nx: number;
  ny: number;
  /** Longueur curviligne cumulée jusqu'à ce point (px). */
  s: number;
  /** Longueur du segment vers le point suivant (px). */
  segLen: number;
  /** Courbure locale (1/rayon, px⁻¹) ≥ 0 : 0 en ligne droite, 1/R en virage. Pour l'IA. */
  curvature: number;
}

/** Porte de checkpoint : segment perpendiculaire à la piste. */
export interface Gate {
  index: number;
  ax: number;
  ay: number;
  bx: number;
  by: number;
  /** Tangente (sens de franchissement valide). */
  tx: number;
  ty: number;
  s: number;
}

/** Construit une centerline circulaire. */
export function buildRing(opts: {
  name: string;
  cx: number;
  cy: number;
  radius: number;
  halfWidth: number;
  laps: number;
  sampleCount: number;
  checkpointCount: number;
}): TrackDef {
  const centerline: Vec2[] = [];
  for (let i = 0; i < opts.sampleCount; i++) {
    const a = (i / opts.sampleCount) * Math.PI * 2;
    centerline.push({ x: opts.cx + opts.radius * Math.cos(a), y: opts.cy + opts.radius * Math.sin(a) });
  }
  return {
    name: opts.name,
    centerline,
    halfWidth: opts.halfWidth,
    laps: opts.laps,
    checkpointCount: opts.checkpointCount,
  };
}

/**
 * Construit une centerline en « stade » : deux longues lignes droites (haut/bas) reliées par
 * deux demi-cercles (gauche/droite). Donne enfin de vraies lignes droites + des virages —
 * bien mieux qu'un simple cercle pour juger la conduite et pour l'IA (gestion par courbure).
 * Sens horaire (à l'écran) ; le départ (sample 0) est à gauche de la droite du haut, cap +x.
 */
export function buildStadium(opts: {
  name: string;
  cx: number;
  cy: number;
  /** Demi-longueur des lignes droites (px). */
  halfStraight: number;
  /** Rayon des virages en bout (px). */
  radius: number;
  halfWidth: number;
  laps: number;
  checkpointCount: number;
  /** Espacement cible entre points de la centerline (px). */
  spacing?: number;
}): TrackDef {
  const { cx, cy, halfStraight: hs, radius: R } = opts;
  const spacing = opts.spacing ?? 14;
  const pts: Vec2[] = [];

  // Chaque segment se sous-échantillonne lui-même et EXCLUT son point final (= début du
  // segment suivant) → pas de doublon, pas de tangente nulle.
  const line = (x0: number, y0: number, x1: number, y1: number): void => {
    const n = Math.max(1, Math.round(Math.hypot(x1 - x0, y1 - y0) / spacing));
    for (let i = 0; i < n; i++) {
      const t = i / n;
      pts.push({ x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * t });
    }
  };
  const arc = (ox: number, oy: number, a0: number, a1: number): void => {
    const n = Math.max(2, Math.round((R * Math.abs(a1 - a0)) / spacing));
    for (let i = 0; i < n; i++) {
      const a = a0 + (a1 - a0) * (i / n);
      pts.push({ x: ox + R * Math.cos(a), y: oy + R * Math.sin(a) });
    }
  };

  const top = cy - R;
  const bot = cy + R;
  // On démarre AU MILIEU de la droite du haut pour que le départ (sample 0) ait une tangente
  // parfaitement horizontale (porte verticale) — pas à une jonction arc/droite (tangente mêlée).
  line(cx, top, cx + hs, top); // demi-droite haut (milieu → droite)
  arc(cx + hs, cy, -Math.PI / 2, Math.PI / 2); // virage droit (haut → bas)
  line(cx + hs, bot, cx - hs, bot); // droite du bas (← entière)
  arc(cx - hs, cy, Math.PI / 2, (3 * Math.PI) / 2); // virage gauche (bas → haut)
  line(cx - hs, top, cx, top); // demi-droite haut (gauche → milieu, ferme la boucle)

  return {
    name: opts.name,
    centerline: pts,
    halfWidth: opts.halfWidth,
    laps: opts.laps,
    checkpointCount: opts.checkpointCount,
  };
}

/**
 * Construit une centerline LISSE depuis des points de contrôle (boucle fermée), via spline
 * Catmull-Rom (la courbe passe par chaque point). Permet des circuits à courbure variable
 * (lignes droites, esses, épingles) — vrai banc d'essai pour l'IA.
 */
export function buildCircuit(opts: {
  name: string;
  waypoints: Vec2[];
  halfWidth: number;
  laps: number;
  checkpointCount: number;
  spacing?: number;
}): TrackDef {
  const wp = opts.waypoints;
  const m = wp.length;
  const spacing = opts.spacing ?? 13;
  const pts: Vec2[] = [];
  // Catmull-Rom CENTRIPÈTE (alpha=0.5, forme de Barry-Goldman) : contrairement à la version
  // uniforme, il ne fait jamais de boucle/cusp (rayon négatif) même sur des angles serrés.
  const knot = (ti: number, a: Vec2, b: Vec2): number =>
    ti + Math.max(1e-3, Math.sqrt(Math.hypot(b.x - a.x, b.y - a.y)));
  for (let i = 0; i < m; i++) {
    const p0 = wp[(i - 1 + m) % m];
    const p1 = wp[i];
    const p2 = wp[(i + 1) % m];
    const p3 = wp[(i + 2) % m];
    const t0 = 0;
    const t1 = knot(t0, p0, p1);
    const t2 = knot(t1, p1, p2);
    const t3 = knot(t2, p2, p3);
    const steps = Math.max(2, Math.round(Math.hypot(p2.x - p1.x, p2.y - p1.y) / spacing));
    for (let k = 0; k < steps; k++) {
      const t = t1 + (t2 - t1) * (k / steps); // segment central [t1,t2), exclut t2 (= début suivant)
      const lx = (ta: number, tb: number, a: number, b: number): number => ((tb - t) * a + (t - ta) * b) / (tb - ta);
      const a1x = lx(t0, t1, p0.x, p1.x), a1y = lx(t0, t1, p0.y, p1.y);
      const a2x = lx(t1, t2, p1.x, p2.x), a2y = lx(t1, t2, p1.y, p2.y);
      const a3x = lx(t2, t3, p2.x, p3.x), a3y = lx(t2, t3, p2.y, p3.y);
      const b1x = lx(t0, t2, a1x, a2x), b1y = lx(t0, t2, a1y, a2y);
      const b2x = lx(t1, t3, a2x, a3x), b2y = lx(t1, t3, a2y, a3y);
      pts.push({ x: lx(t1, t2, b1x, b2x), y: lx(t1, t2, b1y, b2y) });
    }
  }
  return { name: opts.name, centerline: pts, halfWidth: opts.halfWidth, laps: opts.laps, checkpointCount: opts.checkpointCount };
}

export class Track {
  readonly samples: Sample[];
  readonly length: number;
  readonly halfWidth: number;
  readonly laps: number;
  readonly gates: Gate[];
  readonly inner: Vec2[];
  readonly outer: Vec2[];

  constructor(def: TrackDef) {
    const c = def.centerline;
    const n = c.length;
    this.halfWidth = def.halfWidth;
    this.laps = def.laps;

    // Tangentes, normales, longueurs cumulées.
    this.samples = [];
    let s = 0;
    for (let i = 0; i < n; i++) {
      const p = c[i];
      const next = c[(i + 1) % n];
      const prev = c[(i - 1 + n) % n];
      // Abscisse curviligne : on accumule la longueur du segment vers le point SUIVANT.
      const segLen = Math.hypot(next.x - p.x, next.y - p.y) || 1e-6;
      // Tangente par différence CENTRÉE (prev → next) plutôt qu'avant (p → next) : c'est la
      // tangente exacte sur un cercle. Sinon elle est tournée d'un demi-pas d'angle, et la
      // porte de départ (dessinée perpendiculairement) hérite de cette inclinaison (~2°).
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      const len = Math.hypot(dx, dy) || 1e-6;
      const tx = dx / len;
      const ty = dy / len;
      this.samples.push({ x: p.x, y: p.y, tx, ty, nx: -ty, ny: tx, s, segLen, curvature: 0 });
      s += segLen;
    }
    this.length = s;

    // Courbure : taux de rotation de la tangente d'un sample au suivant, par px d'arc.
    // 0 en ligne droite, ~1/R en virage. (Seconde passe : a besoin des tangentes voisines.)
    for (let i = 0; i < n; i++) {
      const a = this.samples[i];
      const b = this.samples[(i + 1) % n];
      const cross = a.tx * b.ty - a.ty * b.tx;
      const dot = a.tx * b.tx + a.ty * b.ty;
      a.curvature = Math.abs(Math.atan2(cross, dot)) / a.segLen;
    }

    // Bords intérieur / extérieur (offset ± normale).
    this.inner = this.samples.map((sm) => ({ x: sm.x - sm.nx * this.halfWidth, y: sm.y - sm.ny * this.halfWidth }));
    this.outer = this.samples.map((sm) => ({ x: sm.x + sm.nx * this.halfWidth, y: sm.y + sm.ny * this.halfWidth }));

    // Portes de checkpoint réparties sur le tour.
    this.gates = [];
    for (let k = 0; k < def.checkpointCount; k++) {
      const idx = Math.round((k * n) / def.checkpointCount) % n;
      const sm = this.samples[idx];
      this.gates.push({
        index: k,
        ax: sm.x + sm.nx * this.halfWidth,
        ay: sm.y + sm.ny * this.halfWidth,
        bx: sm.x - sm.nx * this.halfWidth,
        by: sm.y - sm.ny * this.halfWidth,
        tx: sm.tx,
        ty: sm.ty,
        s: sm.s,
      });
    }
  }

  /** Index du sample le plus proche. Avec `hint`, recherche fenêtrée (perf + anti-hairpin). */
  nearestSampleIndex(x: number, y: number, hint = -1): number {
    const n = this.samples.length;
    let best = 0;
    let bestD = Infinity;
    if (hint >= 0) {
      for (let d = -3; d <= 8; d++) {
        const i = (((hint + d) % n) + n) % n;
        const sm = this.samples[i];
        const dist = (sm.x - x) ** 2 + (sm.y - y) ** 2;
        if (dist < bestD) {
          bestD = dist;
          best = i;
        }
      }
      return best;
    }
    for (let i = 0; i < n; i++) {
      const sm = this.samples[i];
      const dist = (sm.x - x) ** 2 + (sm.y - y) ** 2;
      if (dist < bestD) {
        bestD = dist;
        best = i;
      }
    }
    return best;
  }

  /** Longueur curviligne (s) de la projection du point sur la centerline. */
  arcLengthAt(x: number, y: number, hint = -1): number {
    const i = this.nearestSampleIndex(x, y, hint);
    const a = this.samples[i];
    const t = clamp(((x - a.x) * a.tx + (y - a.y) * a.ty) / a.segLen, 0, 1);
    return a.s + t * a.segLen;
  }

  tangentAt(index: number): Vec2 {
    const sm = this.samples[index];
    return { x: sm.tx, y: sm.ty };
  }

  /**
   * Lignes sur lesquelles construire les corps-murs (épaisseur `thickness`), décalées de
   * `thickness/2` vers l'EXTÉRIEUR de la piste pour que leur face côté piste tombe
   * exactement sur la bordure visuelle (inner/outer). Sans ça, un mur centré sur la
   * bordure déborde de moitié dans la piste -> on cogne avant d'atteindre la bordure.
   */
  boundaryWalls(thickness: number): { inner: Vec2[]; outer: Vec2[] } {
    const off = this.halfWidth + thickness / 2;
    const inner = this.samples.map((s) => ({ x: s.x - s.nx * off, y: s.y - s.ny * off }));
    const outer = this.samples.map((s) => ({ x: s.x + s.nx * off, y: s.y + s.ny * off }));
    return { inner, outer };
  }

  /**
   * Décalage latéral SIGNÉ au centre de piste (px), projeté sur la normale du sample le plus
   * proche : > 0 = côté extérieur (sens de la normale), < 0 = intérieur. |.| > halfWidth => hors-piste.
   */
  signedOffset(x: number, y: number, hint = -1): number {
    const i = this.nearestSampleIndex(x, y, hint);
    const a = this.samples[i];
    return (x - a.x) * a.nx + (y - a.y) * a.ny;
  }

  /** Distance (non signée) au centre de piste ; > halfWidth => hors-piste. */
  distanceToCenter(x: number, y: number, hint = -1): number {
    return Math.abs(this.signedOffset(x, y, hint));
  }

  isInside(x: number, y: number, hint = -1): boolean {
    return this.distanceToCenter(x, y, hint) <= this.halfWidth;
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
